import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import PublicCouponBanner from '../../components/PublicCouponBanner';
import '../../pcos-management-style.css';
import { apiGet, apiPost } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { useNotification } from '../../context/CustomDialogContext';
import { useLeadModal } from '../../context/LeadModalContext';
import CountryPhoneInput from '../../components/CountryPhoneInput';
import CourseEnrollModal from '../../components/CourseEnrollModal';
import courseBgImage from '../../assets/images/courses/course_bg.webp';
import heroCourseImage from '../../assets/images/courses/face-yoga-ttc.webp';

/* ─── Accordion Item (Syllabus & FAQ) ─────────────────────────────────────── */
const AccordionItem = ({ num, title, subtitle, children, isActive, onToggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState('0px');

  useEffect(() => {
    setHeight(isActive ? `${contentRef.current ? contentRef.current.scrollHeight : 500}px` : '0px');
  }, [isActive]);

  return (
    <div className={`fy-accordion-item ${isActive ? 'active' : ''}`}>
      <button
        className="fy-accordion-btn"
        onClick={onToggle}
        aria-expanded={isActive}
        type="button"
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          {num && <span className="fy-accordion-num">{num}</span>}
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span style={{ fontWeight: 700 }}>{title}</span>
            {subtitle && <span className="fy-accordion-subtitle">{subtitle}</span>}
          </span>
        </span>
        <div className="fy-accordion-icon"><i className="bi bi-plus"></i></div>
      </button>
      <div className="fy-accordion-content" style={{ maxHeight: height }}>
        <div ref={contentRef} className="fy-accordion-inner">
          {children}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────────────────── */
const FaceYoga = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCountry, formatPrice } = useCountry();
  const { showAlert } = useNotification();
  const { openModal } = useLeadModal();

  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);

  const courseTitle = "Face Yoga Teacher Training Course";

  const getLogoUrl = () => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
    return `${apiBase}/logo.php`;
  };

  const [activeModule, setActiveModule] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    father_name: '',
    address: '',
    countryCode: '+91',
    phone: '',
    email: '',
    batch_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [dbCourse, setDbCourse] = useState(null);
  const [demoInfo, setDemoInfo] = useState(null);
  const [upcomingBatches, setUpcomingBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  /* Fetch active coupons */
  useEffect(() => {
    async function fetchCoupons() {
      try {
        let couponsRes = await apiGet('/public-banner-coupons.php');
        if (!couponsRes || (Array.isArray(couponsRes) && couponsRes.length === 0) || (couponsRes.data && couponsRes.data.length === 0)) {
          couponsRes = await apiGet('/public-coupons.php');
        }
        let list = [];
        if (Array.isArray(couponsRes)) {
          list = couponsRes;
        } else if (couponsRes && Array.isArray(couponsRes.data)) {
          list = couponsRes.data;
        } else if (couponsRes && Array.isArray(couponsRes.list)) {
          list = couponsRes.list;
        }
        setActiveCoupons(list);
      } catch (err) {
        console.error('Failed to fetch coupons:', err);
      }
    }
    fetchCoupons();
  }, []);

  /* 1-second interval for real-time coupon countdown */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const parseDateSafe = (dateStr) => {
    if (!dateStr) return null;
    let cleanStr = String(dateStr).trim();
    if (!cleanStr) return null;

    let d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    cleanStr = cleanStr.replace('T', ' ').replace(/-/g, '/');
    d = new Date(cleanStr);
    if (!isNaN(d.getTime())) return d;

    d = new Date(cleanStr.replace(/ /g, 'T'));
    if (!isNaN(d.getTime())) return d;

    return null;
  };

  const getBannerCouponForFaceYoga = () => {
    if (!dbCourse) return null;

    const match = activeCoupons.find(
      (c) => Number(c.course_id) === Number(dbCourse.id) || Number(c.course_id) === 0
    );
    if (!match) return null;

    if (match.status && match.status !== 'active') return null;

    const isShowOnPages = match.show_on_pages === undefined || match.show_on_pages === null || match.show_on_pages === 1 || match.show_on_pages === '1' || match.show_on_pages === true;
    if (!isShowOnPages) return null;

    const now = currentTime.getTime();

    if (match.banner_expiry) {
      const expDate = parseDateSafe(match.banner_expiry);
      if (expDate && expDate.getTime() - now <= 0) return null;
    }
    if (match.banner_start) {
      const startDate = parseDateSafe(match.banner_start);
      if (startDate && now < startDate.getTime()) return null;
    }
    if (match.expiry_date) {
      const expDate = parseDateSafe(match.expiry_date);
      if (expDate && expDate.getTime() - now <= 0) return null;
    }

    return match;
  };

  const formatCountdown = (expiryDateStr) => {
    const expDate = parseDateSafe(expiryDateStr);
    if (!expDate) return '';

    const difference = expDate.getTime() - currentTime.getTime();
    if (difference <= 0) return 'Expired';

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    const parts = [];
    if (days > 0) {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    }
    parts.push(`${hours} hr`);
    parts.push(`${minutes} min`);
    parts.push(`${seconds} sec`);

    return parts.join(' ');
  };

  /* Scoped CSS class for design system */
  useLayoutEffect(() => {
    document.body.classList.add('pcos-page');
    return () => { document.body.classList.remove('pcos-page'); };
  }, []);

  /* Load Face Yoga course data & batches & demo class availability */
  useEffect(() => {
    async function loadData() {
      try {
        const coursesData = await apiGet('/public-courses.php');
        let currentCourseId = 0;
        let targetTitle = courseTitle;
        if (coursesData && Array.isArray(coursesData)) {
          let match = coursesData.find(
            c => c.title && c.title.toLowerCase().trim() === courseTitle.toLowerCase().trim()
          );
          if (!match) {
            match = coursesData.find(
              c => (c.slug && c.slug.toLowerCase().trim() === 'face-yoga-teacher-training-course-online') ||
                   (c.course_slug && c.course_slug.toLowerCase().trim() === 'face-yoga-teacher-training-course-online') ||
                   (c.title && c.title.toLowerCase().includes('face yoga'))
            );
          }
          if (match) {
            setDbCourse(match);
            currentCourseId = match.id;
            if (match.title) {
              targetTitle = match.title;
            }
          }
        }

        // Query upcoming batches for Face Yoga
        let batchesData = await apiGet(`/get-upcoming-batches.php?course_title=${encodeURIComponent(targetTitle)}`);
        
        if ((!batchesData || !Array.isArray(batchesData) || batchesData.length === 0) && targetTitle !== courseTitle) {
          const fallbackBatches = await apiGet(`/get-upcoming-batches.php?course_title=${encodeURIComponent(courseTitle)}`);
          if (fallbackBatches && Array.isArray(fallbackBatches) && fallbackBatches.length > 0) {
            batchesData = fallbackBatches;
          }
        }

        if ((!batchesData || !Array.isArray(batchesData) || batchesData.length === 0) && currentCourseId > 0) {
          const idBatches = await apiGet(`/get-upcoming-batches.php?course_id=${currentCourseId}`);
          if (idBatches && Array.isArray(idBatches) && idBatches.length > 0) {
            batchesData = idBatches;
          }
        }

        setUpcomingBatches(batchesData || []);

        try {
          if (currentCourseId > 0) {
            const demoRes = await apiGet(`/public/check-demo-course.php?course_id=${currentCourseId}&slug=face-yoga-teacher-training-course-online`);
            if (demoRes && demoRes.available) {
              setDemoInfo(demoRes);
            }
          }
        } catch (err) {
          console.log("No demo configuration for this course.");
        }
      } catch (err) {
        console.error('Failed to load course details & batches:', err);
      } finally {
        setLoadingBatches(false);
      }
    }
    loadData();
  }, []);

  /* Sync logged-in student data */
  useEffect(() => {
    if (user && user.role === 'student') {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        countryCode: user.country_code || '+91',
        phone: user.mobile_number || user.phone || '',
        father_name: user.father_name || '',
        address: user.address || ''
      }));
    }
  }, [user]);

  /* SEO & smooth scroll */
  useEffect(() => {
    document.title = "Face Yoga Teacher Training Course Online | PranaFlow Academy";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content',
        "Discover the art and science of facial rejuvenation with PranaFlow Academy's Online Face Yoga Teacher Training Course. 30 hours, 1 month, live certification, recorded daily, small batches of 25. Enroll now.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', "https://pranaflowacademy.com/courses/face-yoga-teacher-training-course-online");

    // Open Graph Metadata
    const ogTags = [
      { property: 'og:title', content: "Face Yoga Teacher Training Course Online | PranaFlow Academy" },
      { property: 'og:description', content: "Discover the art and science of facial rejuvenation with PranaFlow Academy's Online Face Yoga Teacher Training Course. 30 hours, 1 month, live certification, recorded daily, small batches of 25. Enroll now." },
      { property: 'og:type', content: "website" },
      { property: 'og:url', content: "https://pranaflowacademy.com/courses/face-yoga-teacher-training-course-online" },
      { property: 'og:image', content: "https://pranaflowacademy.com/images/courses/face-yoga-ttc.webp" },
      { property: 'og:site_name', content: "PranaFlow Academy" }
    ];

    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter / X Metadata
    const twitterTags = [
      { name: 'twitter:card', content: "summary_large_image" },
      { name: 'twitter:title', content: "Face Yoga Teacher Training Course Online | PranaFlow Academy" },
      { name: 'twitter:description', content: "Discover the art and science of facial rejuvenation with PranaFlow Academy's Online Face Yoga Teacher Training Course. 30 hours, 1 month, live certification, recorded daily, small batches of 25. Enroll now." },
      { name: 'twitter:image', content: "https://pranaflowacademy.com/images/courses/face-yoga-ttc.webp" }
    ];

    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash, location.pathname]);

  /* Schema injection */
  useEffect(() => {
    const scriptId = 'jsonld-schema-faceyoga';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    const courseSchema = {
      "@context": "https://schema.org",
      "@type": "Course",
      "@id": "https://pranaflowacademy.com/courses/face-yoga-teacher-training-course-online#course",
      "name": "Face Yoga Teacher Training Course Online",
      "description": "PranaFlow Academy's Face Yoga Teacher Training Course Online teaches face yoga techniques, facial movements, natural lifting practices, lymphatic massage, pranayama, mudras, and teaching methodology for face yoga education.",
      "url": "https://pranaflowacademy.com/courses/face-yoga-teacher-training-course-online",
      "provider": {
        "@id": "https://pranaflowacademy.com/#organization"
      },
      "courseMode": "online",
      "educationalCredentialAwarded": "Certificate of Completion",
      "inLanguage": "en"
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://pranaflowacademy.com/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Courses",
          "item": "https://pranaflowacademy.com/courses"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Face Yoga Teacher Training Course Online",
          "item": "https://pranaflowacademy.com/courses/face-yoga-teacher-training-course-online"
        }
      ]
    };

    script.textContent = JSON.stringify([courseSchema, breadcrumbSchema]);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) existingScript.remove();
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const displayPrice = dbCourse && parseFloat(dbCourse.fee_inr) > 0 ? formatPrice(dbCourse.fee_inr, dbCourse) : (dbCourse ? 'Fee will be updated soon' : 'Loading...');

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { showAlert({ title: "Warning", message: "Student Name is required.", type: "warning" }); return; }
    if (!formData.email.trim()) { showAlert({ title: "Warning", message: "Email address is required.", type: "warning" }); return; }
    if (!formData.countryCode.trim()) { showAlert({ title: "Warning", message: "Country Code is required.", type: "warning" }); return; }
    if (!formData.phone.trim()) { showAlert({ title: "Warning", message: "Please enter your mobile number.", type: "warning" }); return; }

    const isInactive = dbCourse && dbCourse.status === 'inactive';

    if (isInactive) {
      setIsSubmitting(true);
      try {
        const payload = {
          name: formData.name.trim(),
          father_name: formData.father_name ? formData.father_name.trim() : null,
          mobile: formData.phone.trim(),
          country_code: formData.countryCode.trim(),
          mobile_number: formData.phone.trim(),
          country_iso_code: selectedCountry ? selectedCountry.country_code : 'IN',
          email: formData.email.trim(),
          address: formData.address ? formData.address.trim() : null,
          course_id: dbCourse ? dbCourse.id : null,
          country: selectedCountry ? selectedCountry.country_name : null,
          message: "I am interested in this course. Please contact me when new batches are available.",
          source_page: window.location.pathname
        };

        await apiPost('/save-interested-lead.php', payload);
        setIsSuccess(true);
      } catch (err) {
        showAlert({ title: "Error", message: err.message || "Failed to submit details. Please try again.", type: "error" });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!formData.father_name.trim()) { showAlert({ title: "Warning", message: "Father's / Guardian's Name is required.", type: "warning" }); return; }
    if (!formData.address.trim()) { showAlert({ title: "Warning", message: "Full Address is required.", type: "warning" }); return; }
    if (!formData.batch_id) { showAlert({ title: "Warning", message: "Please select an upcoming batch.", type: "warning" }); return; }

    const selectedBatch = upcomingBatches.find(b => Number(b.id) === Number(formData.batch_id));
    if (!selectedBatch) { showAlert({ title: "Warning", message: "Selected batch not found.", type: "warning" }); return; }

    navigate('/enroll-confirm', {
      state: {
        studentDetails: {
          name: formData.name,
          father_name: formData.father_name,
          address: formData.address,
          countryCode: formData.countryCode,
          phone: formData.phone,
          email: formData.email
        },
        courseDetails: {
          id: dbCourse ? dbCourse.id : null,
          title: courseTitle,
          duration: dbCourse ? dbCourse.duration : '30 Hours',
          fee_inr: dbCourse ? dbCourse.fee_inr : 0,
          fee_usd: dbCourse ? dbCourse.fee_usd : 0
        },
        batchDetails: {
          id: selectedBatch.id,
          name: selectedBatch.batch_name,
          timing: selectedBatch.batch_timing,
          start_date: selectedBatch.start_date
        }
      }
    });
  };

  /* 10 Modules Syllabus Data */
  const modules = [
    {
      num: '01',
      title: 'Introduction to Face Yoga',
      desc: 'History, principles, benefits, yogic beauty philosophy, and how Face Yoga supports natural beauty, confidence, and wellness.',
      topics: [
        'Origins & Philosophy of Face Yoga',
        'Principles of Facial Muscle Movement & Muscle Toning',
        'Holistic Beauty vs. Synthetic Treatments',
        'Setting Goals for Personal & Professional Practice'
      ]
    },
    {
      num: '02',
      title: 'Facial Anatomy & Structure',
      desc: 'Understanding facial muscles, skin layers, bones, nerves, pressure points, and the lymphatic system for safe and effective Face Yoga practice.',
      topics: [
        '57+ Facial & Neck Muscle Group Analysis',
        'Facial Skeleton, Skin Layers & Elasticity Factors',
        'Lymphatic Drainage Pathways & Facial Blood Circulation',
        'Key Facial Pressure Points & Marma Locations'
      ]
    },
    {
      num: '03',
      title: 'Understanding Facial Ageing',
      desc: 'Learn the root causes of facial ageing, wrinkles, sagging skin, dullness, habitual facial tension, poor circulation, and natural prevention methods.',
      topics: [
        'Hormonal & Environmental Factors in Facial Ageing',
        'Repetitive Expression Lines & Habitual Tension Patterns',
        'Gravity, Collagen Loss & Muscle Sagging Dynamics',
        'Natural Prevention, Prevention Routines & Youthful Radiance'
      ]
    },
    {
      num: '04',
      title: '85+ Face Yoga Movements',
      desc: 'Zone-by-zone facial exercises covering forehead, eyes, cheeks, lips, jawline, neck, and full-face toning techniques.',
      topics: [
        'Forehead Smoothing & Brow-Lifting Exercises',
        'Eye Area Toning — Dark Circles, Puffiness & Crow’s Feet',
        'Cheek Sculpting & Nasolabial Fold Softening',
        'Lip Area Toning, Jawline Definition & Neck Tightening'
      ]
    },
    {
      num: '05',
      title: 'Natural Facelift & Jawline Sculpting',
      desc: 'Natural face-lifting practices, cheek lifting, jawline improvement methods, facial toning, and sculpting techniques.',
      topics: [
        'Advanced Cheek & Zygomaticus Muscle Workouts',
        'Double Chin Reduction & Jawline Sharpening Protocols',
        'Full-Face Lift Sequences for Daily Practice',
        'Customizing Workouts for Different Face Types & Goals'
      ]
    },
    {
      num: '06',
      title: 'Facial Massage, Acupressure & Lymphatic Drainage',
      desc: 'Lymphatic massage techniques, lymphatic detox, facial massage flows, acupressure points, and techniques to improve circulation and relaxation.',
      topics: [
        'Lymphatic Detox & De-Puffing Massage Sequences',
        'Acupressure Point Activation for Natural Glow & Stress Release',
        'Oil Selection & Skin Type-Specific Massage Strokes',
        'Self-Massage vs. Guided Client Massage Methodologies'
      ]
    },
    {
      num: '07',
      title: 'Breathwork, Pranayama, Mudras & Holistic Tools',
      desc: 'Breathwork and pranayama for facial health, facial relaxation, mudras, facial reflexology, and holistic beauty-supporting practices.',
      topics: [
        'Pranayama Breathing Techniques for Cellular Oxygenation',
        'Beauty Mudras & Energy Channel Activation',
        'Facial Reflexology & Stress-Release Breath Routines',
        'Gua Sha & Jade Roller Integration Guidelines'
      ]
    },
    {
      num: '08',
      title: 'Skin Health, Nutrition & Ayurvedic Beauty',
      desc: 'Nutrition tips for healthy skin, skin glow support, anti-ageing diet planning, Ayurvedic beauty principles, hydration, lifestyle habits, and natural skincare awareness.',
      topics: [
        'Nutritional Foundations for Collagen Production & Glow',
        'Ayurvedic Dosha-Based Skincare & Wellness Routines',
        'Hydration, Sleep & Daily Circadian Habits for Radiance',
        'Natural Skincare Ingredients & Holistic Beauty Habits'
      ]
    },
    {
      num: '09',
      title: 'Wrinkle Softening & Radiance Enhancement',
      desc: 'Techniques to reduce the appearance of fine lines, support glowing skin, relax facial tension, improve facial energy flow, and activate natural beauty from within.',
      topics: [
        'Targeted Wrinkle-Softening Micro-Movements',
        'Facial Tension Release for Jaw Clenching & Stress Lines',
        'Instant Glow Sequences for Morning & Night Routines',
        'Long-Term Youthfulness & Habit Building'
      ]
    },
    {
      num: '10',
      title: 'Teaching Methodology, Practical Assessment & Certification',
      desc: 'How to design and deliver Face Yoga classes, create class sequences, guide students safely, build confidence as an instructor, complete practical assessment, and receive certification.',
      topics: [
        'Structuring 1-on-1 Sessions & Group Face Yoga Workshops',
        'Student Assessment, Face Mapping & Personal Plans',
        'Clear Cueing, Safety Modifications & Mirror Teaching Skills',
        'Practical Assessment & Earning Your PranaFlow Expert Certification'
      ]
    }
  ];

  /* 4 Pillars Summary */
  const pillars = [
    {
      num: '01',
      title: 'Facial Anatomy & Ageing Science',
      tags: ['57+ Facial Muscles', 'Lymphatic Drainage', 'Skin Layers'],
      desc: 'Understand facial structure, muscle groups, blood flow, and the science of natural facial rejuvenation.'
    },
    {
      num: '02',
      title: '85+ Face Yoga Exercises',
      tags: ['Zone Workouts', 'Natural Facelift', 'Jawline Sculpting'],
      desc: 'Master targeted movements for forehead, eyes, cheeks, jawline, neck, and full-face firmness.'
    },
    {
      num: '03',
      title: 'Facial Massage & Acupressure',
      tags: ['Lymphatic Detox', 'Marma Points', 'Breathwork & Mudras'],
      desc: 'Integrate therapeutic massage, acupressure activation, oxygenating breathwork, and Ayurvedic beauty routines.'
    },
    {
      num: '04',
      title: 'Teaching Skills & Certification',
      tags: ['Class Design', 'Live Mentorship', 'Practical Assessment'],
      desc: 'Learn cueing, student face-mapping, workshop design, and earn your PranaFlow Expert Certification.'
    }
  ];

  /* Face Wellbeing Focus Areas */
  const wellbeingPoints = [
    { title: "Forehead lines & furrowed brows", desc: "Habitual expression lines and upper-face tension" },
    { title: "Eye puffiness & dark circles", desc: "Poor circulation and fluid retention around the eye area" },
    { title: "Sagging cheeks & loss of volume", desc: "Decreased cheek muscle tone and facial volume changes" },
    { title: "Nasolabial folds & smile lines", desc: "Deepening folds around mouth and lower face" },
    { title: "Double chin & blurred jawline", desc: "Submental fullness and laxity in neck and jaw area" },
    { title: "Neck lines & tech-neck tension", desc: "Forward head posture lines and neck muscle tightness" },
    { title: "Jaw clenching & TMJ tightness", desc: "Stress-induced jaw tension and nighttime teeth grinding" },
    { title: "Dull, sluggish complexion", desc: "Impaired facial lymphatic drainage and poor skin circulation" },
    { title: "Facial asymmetry", desc: "Uneven chewing habits or asymmetrical expression patterns" },
    { title: "Confusion about facial exercises", desc: "Uncertainty about safe technique, pressure, and frequency" },
    { title: "Overreliance on cosmetic procedures", desc: "Seeking safe, sustainable, natural anti-ageing alternatives" },
    { title: "Lack of structured teaching guidance", desc: "Struggling to design classes or teach Face Yoga professionally" }
  ];

  const highlights = [
    "30 Hours of Intensive Live Online Training",
    "Completed in 1 Month (~1 Hour per Day)",
    "Master 85+ Face Yoga Exercises & Micro-Movements",
    "Complete 10 Structured Educational Modules",
    "Facial Anatomy & Lymphatic Drainage Massage Training",
    "Breathwork, Mudras & Ayurvedic Skincare Principles",
    "Interactive Live Classes with Specialist Educators",
    "PranaFlow Student Dashboard Portal Access",
    "Same-Day Class Recording Access for Catch-Up",
    "Small Interactive Batches (Max 25 Students)",
    "Practical Teaching Assessment & Feedback",
    "PranaFlow Academy Face Yoga Teacher Training Certificate",
    "Flexible Full Fee & Installment Payment Options"
  ];

  const bonuses = [
    "Face Mapping Guide",
    "85+ Movement Manual",
    "Lymphatic Massage Flow",
    "Acupressure Point Chart",
    "Ayurvedic Beauty Routine",
    "Class Sequence Planner",
    "PranaFlow Certificate"
  ];

  const myths = [
    { m: '"Face Yoga causes more wrinkles."', f: 'When performed with proper form, light posture control, and relaxed skin positioning, Face Yoga tones underlying facial muscles without creasing the skin.' },
    { m: '"You need expensive tools or oils for Face Yoga."', f: 'Your hands and facial muscles are the primary tools. Oils support smooth massage glide, but exercises themselves require zero equipment.' },
    { m: '"Face Yoga is only for older individuals."', f: 'Face Yoga is preventive and restorative for all adult ages. Early practice builds healthy facial tone, posture awareness, and skin vitality.' },
    { m: '"Facial exercises work in just 2 days."', f: 'Like any muscular training, facial toning requires consistent daily practice over weeks for visible, lasting muscle firmness and skin radiance.' },
    { m: '"Face Yoga replaces medical dermatological care."', f: 'Face Yoga is a natural wellness and muscle-toning discipline that complements healthy lifestyle habits, not a medical cure for dermatological disease.' },
    { m: '"Anyone can teach Face Yoga without structured training."', f: 'Teaching Face Yoga requires understanding facial anatomy, skin elasticity, pressure safety, and proper cueing to guide students effectively.' }
  ];

  const diffPoints = [
    { title: "Better than free videos", desc: "live posture correction and Q&A instead of guessing from YouTube." },
    { title: "Rooted in Rishikesh", desc: "taught by specialist educators from Rishikesh, led by a YACEP-certified teacher." },
    { title: "Structured 1-month timeline", desc: "30 hours, about 1 hour a day." },
    { title: "Complete coverage", desc: "anatomy, 85+ exercises, massage, acupressure, breathwork, Ayurvedic beauty and teaching." },
    { title: "Same-day recordings", desc: "miss a class, catch up the same day." },
    { title: "Revision during the course", desc: "rewatch technique videos any time while enrolled." },
    { title: "Small batches", desc: "a maximum of 25 students, so the teacher can give you personal feedback." },
    { title: "A real teaching assessment", desc: "you finish this face yoga instructor course by teaching, not just watching." }
  ];

  const faqs = [
    { q: "Do I need any prior yoga or beauty experience to join?", a: "No. This course is beginner-friendly and designed for students from both yoga and beauty backgrounds." },
    { q: "How long is the course?", a: "The course includes 30 hours of learning over approximately one month (~1 hour per day)." },
    { q: "What happens if I miss a live class?", a: "Every live class is recorded and uploaded to your PranaFlow Student Dashboard on the same day so you can catch up easily." },
    { q: "Can I rewatch lectures I have already attended?", a: "Yes. All recorded sessions remain available on your dashboard for revision during your access period." },
    { q: "What certificate do I receive?", a: "You receive a professional Face Yoga Teacher Training Certificate directly from PranaFlow Academy upon successfully completing the required classes, attendance, practical training, assignments, and final assessment." },
    { q: "How many students are in each batch?", a: "Each batch is strictly limited to a maximum of 25 students for personalized guidance." },
    { q: "Can I pay in installments?", a: "Yes, installment payment options are available according to configured course plans." },
    { q: "Can I teach Face Yoga professionally after this course?", a: "Yes, this course is designed to prepare you to begin teaching Face Yoga professionally in workshops, 1-on-1 sessions, or wellness studios." },
    { q: "How does this course complement yoga teacher training?", a: "It expands your wellness skill set by adding facial wellbeing, natural beauty practices, breath awareness, and a popular new student offering to your toolkit." },
    { q: "Will I receive a Student Dashboard?", a: "Yes, after successful enrollment according to the standard PranaFlow Academy LMS account flow." }
  ];

  return (
    <div className="pcos-page public-site">
      <PublicHeader />

      <main className="main">
        <div className="fy-header-spacer"></div>

        {/* 1. HERO SECTION */}
        <section id="hero" className="fy-section">
          <div className="fy-hero-container">
            <div className="fy-hero-wrapper">
              <div className="row align-items-center gy-4">
                <div className="col-lg-6">
                  <span className="fy-hero-pill">
                    <i className="bi bi-star-fill text-warning me-1"></i> Live Online Teacher Training Course
                  </span>
                  <h1 className="fy-hero-title">Face Yoga Teacher Training Course Online</h1>
                  <div className="fy-hero-subhead">Become a Confident Face Yoga Practitioner &amp; Certified Instructor in Just 1 Month</div>

                  <p className="fy-hero-para">
                    PranaFlow Academy's face yoga teacher training online is a 30-hour live face yoga certification course taught by educators from Rishikesh, India. In 1 month you'll learn facial anatomy, 85+ face yoga exercises, facial massage, lymphatic drainage, acupressure, breathwork and how to teach face yoga classes of your own. It's open to complete beginners, yoga teachers, beauty professionals and anyone who wants to practise face yoga properly.
                  </p>

                  <div className="fy-btn-group">
                    <button type="button" className="fy-btn-primary" onClick={() => setIsEnrollModalOpen(true)}>
                      Enroll Now <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                    <button type="button" className="fy-btn-secondary" onClick={() => openModal(courseTitle, dbCourse)}>
                      Enquire Now
                    </button>
                    {demoInfo && demoInfo.available && (
                      <button 
                        type="button"
                        className="fy-btn-secondary" 
                        onClick={() => navigate(`/demo-class?course=${demoInfo.courseSlug || 'face-yoga-teacher-training-course-online'}&course_id=${dbCourse ? dbCourse.id : ''}&demo_course_id=${demoInfo.demoCourseId}`, { state: { course: dbCourse } })}
                      >
                        {demoInfo.demoFee > 0 
                          ? `Demo Class — ₹${Number(demoInfo.demoFee)}` 
                          : 'Demo Class'}
                      </button>
                    )}
                  </div>

                  <div className="fy-trust-badges">
                    <span><i className="bi bi-clock-fill"></i> 30 Hours / 1 Month</span>
                    <span><i className="bi bi-laptop"></i> Live Online Training</span>
                    <span><i className="bi bi-people-fill"></i> Max 25 Per Batch</span>
                    <span><i className="bi bi-award-fill"></i> Expert Certificate</span>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="fy-hero-img-container">
                    <img src={heroCourseImage} alt="Face Yoga Teacher Training Course Online" loading="eager" />
                  </div>
                </div>
              </div>

              {/* Feature Callout Strip */}
              <div className="fy-strip-container">
                <div className="fy-strip-grid">
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-clock-history"></i></div>
                    <div className="fy-strip-label">30 Hours<br />1 Month</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-camera-video-fill"></i></div>
                    <div className="fy-strip-label">Live Online<br />Training</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-journal-bookmark"></i></div>
                    <div className="fy-strip-label">10 Detailed<br />Modules</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-person-lines-fill"></i></div>
                    <div className="fy-strip-label">85+ Face<br />Movements</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon sage"><i className="bi bi-speedometer2"></i></div>
                    <div className="fy-strip-label">Student<br />Dashboard</div>
                  </div>
                  <div className="fy-strip-item">
                    <div className="fy-strip-icon blush"><i className="bi bi-credit-card-2-back"></i></div>
                    <div className="fy-strip-label">Installment<br />Available</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. OVERVIEW — "A comprehensive course designed for real results" */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-plan-box">
              <h2 className="fy-serif-heading mb-4 text-center text-lg-start">A Complete Face Yoga Instructor Course, Taught Live from Rishikesh</h2>

              <div className="row gy-4 align-items-center">
                <div className="col-lg-6">
                  <p className="fy-hero-para">
                    You don't need any yoga or beauty background to join. Some students take this course for their own daily self-care; others want to start teaching face yoga professionally. Either way, you follow the same structured 1-month path.
                  </p>
                  <p className="fy-hero-para">
                    This face yoga teacher training in India is taught live by specialist educators from Rishikesh, India, led by a YACEP-certified teacher. Every class is live, and the class recording will be uploaded to your PranaFlow Student Dashboard the same day, so work, travel or a different time zone won't hold you back.
                  </p>
                </div>

                <div className="col-lg-6">
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>85+ zone-by-zone Face Yoga exercises &amp; micro-movements</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Facial anatomy, muscle Toning &amp; lymphatic drainage</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Natural facelift, jawline sculpting &amp; anti-ageing techniques</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Acupressure points, mudras &amp; breathwork routines</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>Ayurvedic beauty, skin nutrition &amp; natural skincare</span></div>
                  <div className="fy-benefit-pill"><i className="bi bi-check-circle-fill"></i><span>PranaFlow Student Dashboard with daily video access</span></div>
                  <div className="fy-benefit-pill mb-0"><i className="bi bi-check-circle-fill"></i><span>Teaching methodology, practical assessment &amp; certification</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 3. HIGHLIGHTS & BONUS — "What You Will Cover" */}
        <section id="highlights" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">What You Will Cover</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="row gy-4 align-items-stretch">
              <div className="col-lg-8">
                <div className="fy-highlights-grid">
                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Face Yoga Foundations &amp; Yogic Beauty Philosophy</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>57+ Facial Muscle Groups &amp; Anatomy Basics</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Understanding Facial Ageing &amp; Prevention Protocols</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Forehead, Eyebrow &amp; Eye Area Toning Exercises</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Cheek Sculpting &amp; Nasolabial Fold Softening</span></div>
                  </div>

                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Lip Area Toning, Jawline &amp; Double Chin Exercises</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Neck Tightening &amp; Posture Realignment Exercises</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Lymphatic Massage &amp; De-Puffing Massage Flows</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Facial Acupressure Points &amp; Stress Release</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Oxygenating Breathwork &amp; Beauty Mudras</span></div>
                  </div>

                  <div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Ayurvedic Skincare Principles &amp; Skin Nutrition</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Wrinkle-Softening Routines &amp; Facial Glow</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Class Sequencing &amp; Workshop Design Skills</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Practical Teaching Assessment &amp; Feedback</span></div>
                    <div className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>PranaFlow Yoga Expert Teacher Certification</span></div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="fy-bonus-card">
                  <div className="fy-bonus-icon"><i className="bi bi-gift-fill"></i></div>
                  <div className="fy-bonus-title">INCLUDED RESOURCES</div>
                  <div className="fy-bonus-sub">Manuals, Guides &amp; Charts</div>
                  <ul className="fy-bonus-list">
                    {bonuses.map((b, i) => (
                      <li key={i}><i className="bi bi-check2-circle"></i>{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. CURRICULUM OVERVIEW — 10 Modules Accordion */}
        <section id="curriculum" className="fy-section">
          <div className="fy-container">
            {/* 10-Module Syllabus Accordion */}
            <div className="fy-section-title-wrap mb-4">
              <h3 className="fy-serif-heading" style={{ fontSize: '1.6rem' }}>Complete 10-Module Detailed Syllabus</h3>
              <p className="fy-hero-para" style={{ maxWidth: '650px', margin: '8px auto 0' }}>
                Click on any module to view the topics covered in the 30-hour live curriculum.
              </p>
            </div>

            <div className="fy-syllabus-container">
              {modules.map((m, idx) => (
                <AccordionItem
                  key={idx}
                  num={m.num}
                  title={m.title}
                  isActive={activeModule === idx}
                  onToggle={() => setActiveModule(activeModule === idx ? null : idx)}
                >
                  <p style={{ fontSize: '0.9rem', color: 'var(--fy-text-main)', marginBottom: '12px', lineHeight: 1.65 }}>
                    {m.desc}
                  </p>
                  <h5 style={{ fontWeight: 700, color: 'var(--fy-sage-dark)', fontSize: '0.92rem', marginBottom: '10px' }}>Topics Covered:</h5>
                  <ul style={{ paddingLeft: '20px', color: 'var(--fy-text-muted)', fontSize: '0.88rem', lineHeight: 1.65, margin: 0 }}>
                    {m.topics.map((top, tIdx) => (
                      <li key={tIdx} style={{ marginBottom: '6px' }}>{top}</li>
                    ))}
                  </ul>
                </AccordionItem>
              ))}
            </div>
          </div>
        </section>

        {/* 5. AUDIENCE SECTION — WHO IS THIS FACE YOGA TEACHER TRAINING FOR? */}
        <section id="who-is-this-for" className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap mb-5">
              <h2 className="fy-serif-heading">Who Is This Face Yoga Teacher Training For?</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '650px', margin: '12px auto 0' }}>
                Three kinds of students join each batch. Find yours.
              </p>
            </div>

            <div className="row gy-4 align-items-stretch">
              {/* Card 1 */}
              <div className="col-lg-4 col-md-6 col-12">
                <div style={{
                  background: '#ffffff',
                  border: '1px solid var(--fy-sage-border, #d1e2d7)',
                  borderRadius: '16px',
                  padding: '28px 24px',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: '100%'
                }}>
                  <div>
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      background: 'var(--fy-sage-light, #e8f3ed)',
                      color: 'var(--fy-sage-primary, #2d5a43)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      marginBottom: '16px'
                    }}>
                      <i className="bi bi-sparkles" aria-hidden="true"></i>
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--fy-sage-dark, #1b4d3e)', marginBottom: '10px' }}>
                      For your own glow &amp; self-care
                    </h3>
                    <p style={{ fontStyle: 'italic', color: 'var(--fy-text-muted, #4b5563)', marginBottom: '14px', fontSize: '0.95rem' }}>
                      “I want to look fresh and cared for, without a complicated routine.”
                    </p>
                    <ul style={{ paddingLeft: '20px', marginBottom: '16px', color: 'var(--fy-text-main, #374151)', fontSize: '0.92rem', lineHeight: '1.6' }}>
                      <li>Your face looks tired or dull after long workdays and screen time</li>
                      <li>You wake up puffy, or hold tension in your forehead and jaw</li>
                      <li>You've tried free videos but never stuck with them</li>
                    </ul>
                    <p style={{ color: 'var(--fy-text-main, #374151)', marginBottom: '20px', fontSize: '0.92rem', lineHeight: '1.65' }}>
                      You'll learn a simple daily practice, taught live, with massage and lymphatic drainage techniques you can do at home. Classes are about 1 hour a day, and the recording is on your dashboard the same day if you miss one.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="fy-btn-primary"
                    onClick={() => setIsEnrollModalOpen(true)}
                    style={{ padding: '12px 20px', fontSize: '0.9rem', width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    Start My Practice →
                  </button>
                </div>
              </div>

              {/* Card 2 */}
              <div className="col-lg-4 col-md-6 col-12">
                <div style={{
                  background: '#ffffff',
                  border: '1px solid var(--fy-sage-border, #d1e2d7)',
                  borderRadius: '16px',
                  padding: '28px 24px',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: '100%'
                }}>
                  <div>
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      background: 'var(--fy-blush-light, #fcefe8)',
                      color: 'var(--fy-blush-dark, #a85d38)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      marginBottom: '16px'
                    }}>
                      <i className="bi bi-heart" aria-hidden="true"></i>
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--fy-sage-dark, #1b4d3e)', marginBottom: '10px' }}>
                      For the age-positive woman
                    </h3>
                    <p style={{ fontStyle: 'italic', color: 'var(--fy-text-muted, #4b5563)', marginBottom: '14px', fontSize: '0.95rem' }}>
                      “I want to look refreshed, naturally, without false promises.”
                    </p>
                    <ul style={{ paddingLeft: '20px', marginBottom: '16px', color: 'var(--fy-text-main, #374151)', fontSize: '0.92rem', lineHeight: '1.6' }}>
                      <li>You're noticing fine lines or tension around your lower face and jawline</li>
                      <li>You'd rather try a natural, non-invasive approach first</li>
                      <li>You're tired of "look 20 again" claims</li>
                    </ul>
                    <p style={{ color: 'var(--fy-text-main, #374151)', marginBottom: '20px', fontSize: '0.92rem', lineHeight: '1.65' }}>
                      You'll learn face yoga built on facial anatomy and muscle awareness, with routines for the lower face, neck and jaw designed to support a relaxed, refreshed appearance. An expert corrects your form live.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="fy-btn-primary"
                    onClick={() => scrollTo('curriculum')}
                    style={{ padding: '12px 20px', fontSize: '0.9rem', width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    Explore the Course →
                  </button>
                </div>
              </div>

              {/* Card 3 */}
              <div className="col-lg-4 col-md-6 col-12">
                <div style={{
                  background: '#ffffff',
                  border: '1px solid var(--fy-sage-border, #d1e2d7)',
                  borderRadius: '16px',
                  padding: '28px 24px',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: '100%'
                }}>
                  <div>
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      background: 'var(--fy-sage-light, #e8f3ed)',
                      color: 'var(--fy-sage-primary, #2d5a43)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      marginBottom: '16px'
                    }}>
                      <i className="bi bi-award" aria-hidden="true"></i>
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--fy-sage-dark, #1b4d3e)', marginBottom: '10px' }}>
                      For yoga teachers &amp; wellness professionals
                    </h3>
                    <p style={{ fontStyle: 'italic', color: 'var(--fy-text-muted, #4b5563)', marginBottom: '14px', fontSize: '0.95rem' }}>
                      “I want to add a skill my students and clients will love.”
                    </p>
                    <ul style={{ paddingLeft: '20px', marginBottom: '16px', color: 'var(--fy-text-main, #374151)', fontSize: '0.92rem', lineHeight: '1.6' }}>
                      <li>You teach yoga, fitness or beauty and want a fresh offering</li>
                      <li>You want proper training in anatomy and cueing, not copied routines</li>
                      <li>You're wondering how to become a face yoga teacher with a proper certificate</li>
                    </ul>
                    <p style={{ color: 'var(--fy-text-main, #374151)', marginBottom: '20px', fontSize: '0.92rem', lineHeight: '1.65' }}>
                      This face yoga instructor course covers anatomy, 85+ exercises, massage, acupressure and how to sequence and teach a class, ending with a practical teaching assessment and your PranaFlow Academy certificate.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="fy-btn-primary"
                    onClick={() => setIsEnrollModalOpen(true)}
                    style={{ padding: '12px 20px', fontSize: '0.9rem', width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    Become a Face Yoga Teacher →
                  </button>
                </div>
              </div>
            </div>

            <div className="small text-muted fst-italic text-center mt-4" style={{ maxWidth: '800px', margin: '24px auto 0' }}>
              *PranaFlow Academy provides structured wellness education. Individual results vary with consistent practice and lifestyle.*
            </div>
          </div>
        </section>

        {/* 6. DEDICATED PROGRAM DETAILS & ENROLLMENT SECTION */}
        <section id="course-details" className="fy-section fy-section-alt">
          <div className="fy-container" id="pricing">
            <div className="fy-section-title-wrap mb-4">
              <h2 className="fy-serif-heading">Face Yoga Course Fees &amp; Next Batch</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="row gy-4 align-items-start justify-content-center">
              {/* Left Side: The existing PROGRAM DETAILS card */}
              <div className="col-lg-6 col-xl-5">
                {(() => {
                  const coupon = getBannerCouponForFaceYoga();
                  const feeInr = dbCourse ? parseFloat(dbCourse.fee_inr) : 0;
                  let formattedOriginalPrice = displayPrice;
                  let formattedDiscountedPrice = '';
                  let percentageVal = 0;

                  if (coupon && feeInr > 0) {
                    if (coupon.discount_type === 'percentage') {
                      percentageVal = Math.round(parseFloat(coupon.discount_value));
                      const discountedInr = feeInr * (1 - percentageVal / 100);
                      formattedDiscountedPrice = formatPrice(discountedInr, dbCourse);
                      formattedOriginalPrice = formatPrice(feeInr, dbCourse);
                    } else if (coupon.discount_type === 'flat' || coupon.discount_type === 'fixed') {
                      const fixedVal = parseFloat(coupon.discount_value);
                      const discountedInr = Math.max(0, feeInr - fixedVal);
                      percentageVal = Math.round((fixedVal / feeInr) * 100);
                      formattedDiscountedPrice = formatPrice(discountedInr, dbCourse);
                      formattedOriginalPrice = formatPrice(feeInr, dbCourse);
                    }
                  }

                  return (
                    <div className="fy-program-details-card-v2" id="pricing">
                      <h3 className="pcos-pd-heading">PROGRAM DETAILS</h3>
                      <div className="pcos-pd-heading-divider"></div>

                      <div className="pcos-pd-rows-wrap">
                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-calendar-event"></i>
                            <span>Duration</span>
                          </div>
                          <div className="pcos-pd-val">30 Hours / 1 Month</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-laptop"></i>
                            <span>Mode</span>
                          </div>
                          <div className="pcos-pd-val">Live Online Training</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-clock"></i>
                            <span>Timing</span>
                          </div>
                          <div className="pcos-pd-val">~1 Hour / Day</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-phone"></i>
                            <span>Batch Size</span>
                          </div>
                          <div className="pcos-pd-val">Max 25 Students</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-check2-square"></i>
                            <span>Curriculum</span>
                          </div>
                          <div className="pcos-pd-val">10 Modules · 85+ Exercises</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-speedometer2"></i>
                            <span>Portal</span>
                          </div>
                          <div className="pcos-pd-val">PranaFlow Dashboard</div>
                        </div>

                        <div className="pcos-pd-row">
                          <div className="pcos-pd-label">
                            <i className="bi bi-award"></i>
                            <span>Certificate</span>
                          </div>
                          <div className="pcos-pd-val">PranaFlow Expert Cert</div>
                        </div>
                      </div>

                      <div className="pf-course-promo-badge pf-course-promo-badge-pd">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                          <line x1="7" y1="7" x2="7.01" y2="7"></line>
                        </svg>
                        <span>Every batch: First 5 students get an extra discount on fee!</span>
                      </div>

                      {coupon && formattedDiscountedPrice ? (
                        <div className="pcos-pd-offer-box">
                          <div className="pcos-pd-offer-pill">
                            <i className="bi bi-star"></i> LIMITED TIME OFFER
                          </div>

                          <div className="pcos-pd-offer-prices-row">
                            <div className="pcos-pd-orig-price-wrap">
                              <span className="pcos-pd-metric-label">ORIGINAL FEE</span>
                              <span className="pcos-pd-orig-price">{formattedOriginalPrice}</span>
                            </div>

                            <div className="pcos-pd-disc-price-wrap">
                              <span className="pcos-pd-metric-label">DISCOUNTED FEE</span>
                              <span className="pcos-pd-disc-price">{formattedDiscountedPrice}</span>
                            </div>

                            <div className="pcos-pd-badge-wrap">
                              <div className="pcos-pd-badge-rect">
                                <span className="pcos-pd-badge-num">{percentageVal}%</span>
                                <span className="pcos-pd-badge-text">OFF</span>
                              </div>
                            </div>
                          </div>

                          {(coupon.banner_expiry || coupon.expiry_date) && (
                            <div className="pcos-pd-timer-strip">
                              <i className="bi bi-stopwatch"></i>
                              <span>
                                {formatCountdown(coupon.banner_expiry || coupon.expiry_date).includes('day') ? 'Offer ends in' : 'Offer valid today'} · {formatCountdown(coupon.banner_expiry || coupon.expiry_date)}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="pcos-pd-normal-box">
                          <span className="pcos-pd-normal-label">TUITION FEE</span>
                          <span className="pcos-pd-normal-price">{loadingBatches ? 'Loading...' : displayPrice}</span>
                        </div>
                      )}

                      <button
                        type="button"
                        className="fy-btn-secondary w-100 mt-2"
                        onClick={() => openModal(courseTitle, dbCourse)}
                        style={{
                          borderRadius: '30px',
                          padding: '12px 20px',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        Enquire Now <i className="bi bi-chat-dots"></i>
                      </button>

                      <div className="pcos-pd-next-batch-strip">
                        <i className="bi bi-leaf"></i>
                        <span>Next batch admissions open — inquire now</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Right Side: The existing enrolment form & demo invitation */}
              <div className="col-lg-6 col-xl-5">
                <CourseEnrollModal
                  inline={true}
                  isOpen={true}
                  onClose={() => {}}
                  courseTitle={courseTitle}
                  formData={formData}
                  setFormData={setFormData}
                  upcomingBatches={upcomingBatches}
                  isSubmitting={isSubmitting}
                  isSuccess={isSuccess}
                  handleEnrollSubmit={handleEnrollSubmit}
                />

                {/* Demo Class Invitation Callout */}
                {demoInfo && demoInfo.available && (
                  <div
                    style={{
                      marginTop: '16px',
                      padding: '16px 20px',
                      borderRadius: '16px',
                      background: 'rgba(45, 90, 67, 0.06)',
                      border: '1px solid var(--fy-sage-border, #d1e2d7)',
                      fontSize: '0.88rem',
                      color: 'var(--fy-text-main, #1f2937)',
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <div>
                      Not sure yet? Join a <strong style={{ color: 'var(--fy-sage-primary, #2d5a43)' }}>demo class for {demoInfo.demoFee > 0 ? `₹${Number(demoInfo.demoFee)}` : 'free'}</strong> and meet the teacher first.
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/demo-class?course=${demoInfo.courseSlug || 'face-yoga-teacher-training-course-online'}&course_id=${dbCourse ? dbCourse.id : ''}&demo_course_id=${demoInfo.demoCourseId}`, { state: { course: dbCourse } })}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--fy-sage-primary, #2d5a43)',
                        textDecoration: 'underline',
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: '4px 8px',
                        fontSize: '0.88rem'
                      }}
                    >
                      Book Demo Class →
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* 6. FACIAL WELLBEING FOCUS AREAS */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap text-center">
              <h2 className="fy-serif-heading">Common Face Yoga Concerns We Help You Work On</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '750px', margin: '12px auto 0' }}>
                Most students arrive with one or more of these. The course gives you a structured, expert-led way to practise, instead of guessing from random videos.
              </p>
            </div>

            <div className="row gy-4">
              {/* Group 1: Your face & skin */}
              <div className="col-lg-6">
                <div style={{
                  background: '#ffffff',
                  border: '1px solid var(--fy-sage-border, #d1e2d7)',
                  borderRadius: '16px',
                  padding: '28px 24px',
                  height: '100%',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)'
                }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--fy-sage-dark, #1b4d3e)', marginBottom: '18px' }}>
                    Your face &amp; skin
                  </h3>
                  <ul style={{ paddingLeft: '20px', margin: 0, display: 'flex', flexDirection: 'column', gap: '14px', color: 'var(--fy-text-main, #374151)', fontSize: '0.95rem', lineHeight: '1.6' }}>
                    <li>
                      <strong>Tired, dull-looking skin:</strong> massage and lymphatic drainage to support circulation and a fresher look
                    </li>
                    <li>
                      <strong>Morning puffiness:</strong> gentle drainage routines for the eyes and cheeks
                    </li>
                    <li>
                      <strong>Forehead and brow tension:</strong> relaxation for habitual frowning and expression lines
                    </li>
                    <li>
                      <strong>Jawline and double chin area:</strong> face yoga for jawline and lower-face muscle awareness, plus jaw-release techniques
                    </li>
                    <li>
                      <strong>Neck and "tech-neck" strain:</strong> posture and neck routines for long screen hours
                    </li>
                  </ul>
                </div>
              </div>

              {/* Groups 2 & 3: Your practice & Your teaching */}
              <div className="col-lg-6 d-flex flex-column gap-4">
                {/* Group 2: Your practice */}
                <div style={{
                  background: '#ffffff',
                  border: '1px solid var(--fy-sage-border, #d1e2d7)',
                  borderRadius: '16px',
                  padding: '24px',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
                  flex: 1
                }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--fy-sage-dark, #1b4d3e)', marginBottom: '16px' }}>
                    Your practice
                  </h3>
                  <ul style={{ paddingLeft: '20px', margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', color: 'var(--fy-text-main, #374151)', fontSize: '0.95rem', lineHeight: '1.6' }}>
                    <li>
                      <strong>Not sure what's safe:</strong> learn correct technique, pressure and frequency
                    </li>
                    <li>
                      <strong>Can't stay consistent:</strong> daily live classes and a 1-month structure keep you on track
                    </li>
                  </ul>
                </div>

                {/* Group 3: Your teaching */}
                <div style={{
                  background: '#ffffff',
                  border: '1px solid var(--fy-sage-border, #d1e2d7)',
                  borderRadius: '16px',
                  padding: '24px',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
                  flex: 1
                }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--fy-sage-dark, #1b4d3e)', marginBottom: '16px' }}>
                    Your teaching
                  </h3>
                  <ul style={{ paddingLeft: '20px', margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', color: 'var(--fy-text-main, #374151)', fontSize: '0.95rem', lineHeight: '1.6' }}>
                    <li>
                      <strong>Want to teach but lack structure:</strong> learn to design, sequence and cue a face yoga class
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div style={{ background: 'var(--fy-cream-card)', border: '2px dashed var(--fy-sage-border)', borderRadius: 'var(--fy-radius-lg)', padding: '24px 32px', textAlign: 'center', marginTop: '32px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--fy-sage-primary)', marginBottom: '8px' }}>
                “Natural care is about understanding your face, practising consistently and treating yourself with patience.”
              </h3>
              <p style={{ color: 'var(--fy-text-muted)', margin: 0, fontSize: '0.95rem' }}>
                Our 1-month certification introduces that structured, evidence-guided system so you can practice and teach with confidence.
              </p>
            </div>
          </div>
        </section>

        {/* 7. PRANAFLOW STUDENT DASHBOARD */}
        <section className="fy-section fy-dashboard-section">
          <div className="fy-container">
            <div className="row align-items-center gy-5">
              <div className="col-lg-7">
                <span className="fy-dashboard-badge">PranaFlow LMS Student Portal</span>
                <h2 className="fy-serif-heading mb-3" style={{ color: '#ffffff', fontSize: '2rem' }}>Missed a Live Class? Your Learning Continues.</h2>
                <p className="fy-hero-para" style={{ color: '#C3DFC8' }}>
                  Work, travel, or time-zone differences should never disrupt your training. Upon enrollment, you receive full access to your private <strong style={{ color: '#ffffff' }}>PranaFlow Student Dashboard</strong>.
                </p>

                <div className="row gy-2 mb-4" style={{ fontSize: '0.95rem', color: '#E8F5EB' }}>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Daily Live Lecture Links &amp; Timings</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Same-Day Class Video Recordings</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Missed-Class Catch-Up Portal</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Technique Revision Access</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Downloadable Movement Guides &amp; Charts</span></div></div>
                  <div className="col-sm-6"><div className="fy-check-bullet" style={{ color: '#E8F5EB' }}><i className="bi bi-check-circle-fill" style={{ color: '#A3D4B0' }}></i><span>Course Progress &amp; Assessment Portal</span></div></div>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 'var(--fy-radius-md)', padding: '20px 24px' }}>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fbbf24', margin: '0 0 6px' }}>
                    Learn Live. Catch Up When Needed. Master Every Technique.
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#C3DFC8', lineHeight: 1.6 }}>
                    Every live lecture is recorded and uploaded to your Student Dashboard on the same day so you can stay fully aligned with your batch.
                  </p>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="fy-dashboard-card">
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--fy-sage-primary)', marginBottom: '16px' }}>Your Student Dashboard Includes:</h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.9rem', color: 'var(--fy-text-main)' }}>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Enrolled Face Yoga Course Access</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Daily Live Class Schedule &amp; Links</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>10 Structured Course Modules</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Uploaded Lecture Recordings</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Anatomy Charts &amp; Exercise Manuals</span></li>
                    <li className="fy-check-bullet"><i className="bi bi-check-circle-fill"></i><span>Practical Assessment Submission</span></li>
                  </ul>
                  <button type="button" className="fy-btn-primary w-100" onClick={() => scrollTo('enrollment')}>
                    Get Access Upon Enrollment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 8. MYTH-BUSTING */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Face Yoga Myth-Busting</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '600px', margin: '12px auto 0' }}>
                Replacing internet claims with anatomical and holistic clarity.
              </p>
            </div>

            <div className="row gy-3">
              {myths.map((item, idx) => (
                <div key={idx} className="col-md-6 col-lg-4">
                  <div className="fy-myth-card">
                    <span className="fy-myth-badge">MYTH vs FACT</span>
                    <h4 className="fy-myth-q">{item.m}</h4>
                    <p className="fy-myth-a"><strong>Fact:</strong> {item.f}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 9. WHY THIS COURSE IS DIFFERENT */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Why Choose PranaFlow's Face Yoga Teacher Training in India</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-diff-grid">
              {diffPoints.map((card, idx) => (
                <div key={idx} className="fy-diff-card">
                  <h4 className="fy-diff-title">{card.title}</h4>
                  <p className="fy-diff-desc">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 10. 1-MONTH ROADMAP */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Your 1-Month Progression Journey</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-roadmap-grid">
              <div className="fy-roadmap-card month1">
                <div>
                  <span className="fy-roadmap-badge month1">Week 1</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Foundations &amp; Facial Anatomy</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Module 01 — Introduction &amp; Philosophy</li>
                    <li>Module 02 — Facial Muscles &amp; Anatomy</li>
                    <li>Module 03 — Facial Ageing &amp; Prevention</li>
                  </ul>
                </div>
                <div style={{ background: 'var(--fy-sage-light)', border: '1px solid var(--fy-sage-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: 'var(--fy-sage-dark)', fontWeight: 600 }}>
                  Goal: Understand muscle groups &amp; form.
                </div>
              </div>

              <div className="fy-roadmap-card month2">
                <div>
                  <span className="fy-roadmap-badge month2">Weeks 2–3</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Movements, Facelift &amp; Massage</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Module 04 — 85+ Zone-by-Zone Exercises</li>
                    <li>Module 05 — Facelift &amp; Jawline Sculpting</li>
                    <li>Module 06 — Lymphatic Massage &amp; Acupressure</li>
                    <li>Module 07 — Breathwork, Mudras &amp; Tools</li>
                  </ul>
                </div>
                <div style={{ background: 'var(--fy-blush-light)', border: '1px solid var(--fy-blush-border)', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: 'var(--fy-blush-dark)', fontWeight: 600 }}>
                  Goal: Master zone exercises &amp; massage flows.
                </div>
              </div>

              <div className="fy-roadmap-card month3">
                <div>
                  <span className="fy-roadmap-badge month3">Week 4</span>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--fy-text-main)', margin: '0 0 10px' }}>Skincare, Teaching &amp; Certification</h3>
                  <ul className="fy-week-list" style={{ marginBottom: '14px' }}>
                    <li>Module 08 — Skin Nutrition &amp; Ayurveda</li>
                    <li>Module 09 — Wrinkle Softening Routines</li>
                    <li>Module 10 — Class Sequencing &amp; Assessment</li>
                  </ul>
                </div>
                <div style={{ background: '#DCF5E6', border: '1px solid #A7D3B6', padding: '10px', borderRadius: 'var(--fy-radius-sm)', fontSize: '0.82rem', color: '#1A5C33', fontWeight: 600 }}>
                  Goal: Complete assessment &amp; earn certification.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 11. ENROLLMENT & SEAT RESERVATION */}
        <section id="pricing-form" className="fy-section">
          <div className="fy-container text-center">
            <div id="enrollment" className="py-2">
              <div className="fy-section-title-wrap text-center mb-3">
                <h2 className="fy-serif-heading" style={{ fontSize: '2rem' }}>
                  Enroll in the Next Face Yoga Certification Course Batch
                </h2>
                <div className="fy-title-underline"></div>
              </div>

              <p style={{ fontSize: '1.02rem', color: 'var(--fy-text-muted)', maxWidth: '880px', margin: '0 auto 36px', lineHeight: 1.75 }}>
                Each batch is capped at 25 students so the teacher can correct everyone's technique personally. Seats fill on a first-come basis. Reserve yours now, or try a demo class first.
              </p>

              <div className="fy-enrollment-cta-group">
                <button
                  type="button"
                  className="fy-btn-primary py-3 px-4"
                  style={{ borderRadius: '30px', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}
                  onClick={() => setIsEnrollModalOpen(true)}
                >
                  Enroll Now &amp; Reserve Your Seat <i className="bi bi-arrow-right-circle-fill ms-1"></i>
                </button>

                <button
                  type="button"
                  className="fy-btn-secondary py-3 px-4"
                  style={{ borderRadius: '30px', fontWeight: 700, fontSize: '0.95rem', whiteSpace: 'nowrap' }}
                  onClick={() => openModal(courseTitle, dbCourse)}
                >
                  Enquire Now <i className="bi bi-chat-dots ms-1"></i>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* 12. WHAT YOU RECEIVE */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <span className="fy-eyebrow">EVERYTHING INCLUDED</span>
              <h2 className="fy-serif-heading">What's Included in Your Face Yoga Teacher Training Online</h2>
              <div className="fy-title-underline"></div>
              <p className="fy-hero-para mt-3" style={{ maxWidth: '650px', margin: '12px auto 0' }}>
                Everything included in the Online Face Yoga Teacher Training Course from the moment you enroll.
              </p>
            </div>

            <div className="fy-receive-grid">
              {highlights.map((item, idx) => (
                <div key={idx} className="fy-receive-card">
                  <div className="fy-receive-icon">
                    <i className="bi bi-check-lg"></i>
                  </div>
                  <span className="fy-receive-text">{item}</span>
                </div>
              ))}
            </div>

            <div className="fy-receive-assurance">
              <i className="bi bi-shield-check"></i>
              <span>All study materials, live class links, and dashboard access are unlocked upon registration.</span>
            </div>
          </div>
        </section>

        {/* 12.5 CERTIFICATE SECTION */}
        <section className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="row align-items-center gy-4">
              <div className="col-lg-7">
                <span className="fy-eyebrow">ACADEMY CERTIFICATION</span>
                <h2 className="fy-serif-heading mb-3">Face Yoga Teacher Training — Professional Certification</h2>
                <p className="fy-hero-para mb-3">
                  Your <strong>Face Yoga Teacher Training Certificate</strong> is issued directly by <strong>PranaFlow Academy</strong> after successful completion of the required training and assessment.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  The course combines structured theoretical and practical learning in face yoga techniques, facial anatomy, facial muscle awareness, natural face-lifting practices, lymphatic drainage, facial massage, acupressure, relaxation techniques, skin and lifestyle education, and face yoga teaching methodology.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  After successfully completing the required classes, attendance, practical training, assignments, and final assessment, you will receive a <strong>professional Face Yoga Teacher Training Certificate from PranaFlow Academy</strong>.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1rem' }}>
                  The certificate confirms successful completion of the PranaFlow Academy Face Yoga Teacher Training program and can be used as evidence of your professional training in Face Yoga.
                </p>
                <p style={{ color: 'var(--fy-text-main)', fontSize: '0.95rem', lineHeight: '1.65', marginBottom: '1.5rem' }}>
                  This is a <strong>PranaFlow Academy professional certification</strong> and is not presented as a Yoga Alliance teacher credential.
                </p>

                <div className="fy-trust-badges mt-2 mb-2" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', background: 'var(--fy-cream-card)', padding: '12px 20px', borderRadius: '30px', border: '1px solid var(--fy-sage-border)', fontSize: '0.88rem', fontWeight: '600', color: 'var(--fy-sage-dark)' }}>
                  <i className="bi bi-patch-check-fill text-success" style={{ fontSize: '1.1rem' }}></i>
                  <span>Professional Face Yoga Training · Practical Certification · PranaFlow Academy Certificate</span>
                </div>
              </div>

              <div className="col-lg-5 text-center">
                <div style={{ background: 'var(--fy-cream-card)', border: '2px solid var(--fy-sage-border)', borderRadius: 'var(--fy-radius-lg)', padding: '36px 28px', boxShadow: 'var(--fy-shadow-card)' }}>
                  <i className="bi bi-award-fill" style={{ fontSize: '3.8rem', color: 'var(--fy-sage-primary)' }}></i>
                  <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--fy-sage-dark)', marginTop: '16px', marginBottom: '8px' }}>
                    Face Yoga Teacher Training Certificate
                  </h3>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--fy-sage-primary)', marginBottom: '16px' }}>Issued Directly by PranaFlow Academy</div>
                  <p style={{ fontSize: '0.88rem', color: 'var(--fy-text-main)', lineHeight: 1.6, margin: 0 }}>
                    Awarded upon completing 1 month of structured live online training, practical training, facial anatomy, assignments, and final assessment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 13. FAQ */}
        <section id="faq" className="fy-section fy-section-alt">
          <div className="fy-container">
            <div className="fy-section-title-wrap">
              <h2 className="fy-serif-heading">Frequently Asked Questions</h2>
              <div className="fy-title-underline"></div>
            </div>

            <div className="fy-faq-grid">
              {faqs.map((faq, idx) => (
                <div key={idx} className="fy-faq-item">
                  <button className="fy-faq-btn" aria-expanded={activeFaq === idx}
                    type="button"
                    onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}>
                    <span>{faq.q}</span>
                    <div className="fy-faq-icon"><i className="bi bi-plus"></i></div>
                  </button>
                  {activeFaq === idx && (
                    <div className="fy-faq-ans" style={{ display: 'block' }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 14. FINAL CTA BANNER */}
        <section className="fy-section">
          <div className="fy-container">
            <div className="fy-cta-banner">
              <div className="row gy-4 align-items-center">
                <div className="col-lg-8">
                  <h2 className="fy-cta-heading">Master Natural Facial Rejuvenation &amp; Share It with the World.</h2>
                  <p className="fy-cta-sub">Begin Your 1-Month Face Yoga Certification Journey Today.</p>
                  <p style={{ fontSize: '0.95rem', color: '#C3DFC8', lineHeight: 1.7, marginBottom: '20px' }}>
                    Over the next 1 month, learn 85+ exercises, facial anatomy, lymphatic massage, breathwork, Ayurvedic beauty, and professional teaching skills with live expert guidance managed in your PranaFlow Student Dashboard.
                  </p>
                  <div className="fy-btn-group">
                    <button type="button" className="fy-btn-primary fy-cta-btn-white" onClick={() => setIsEnrollModalOpen(true)}>
                      Enroll Now <i className="bi bi-arrow-right-circle-fill"></i>
                    </button>
                    <button type="button" className="fy-btn-secondary fy-cta-btn-outline" onClick={() => openModal(courseTitle, dbCourse)}>
                      Enquire Now
                    </button>
                  </div>
                </div>
                <div className="col-lg-4 text-center">
                  <div style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 'var(--fy-radius-lg)', padding: '24px 20px' }}>
                    <div className="fy-cta-logo-wrap">
                      <img 
                        src={getLogoUrl()} 
                        alt="PranaFlow Academy" 
                        className="fy-cta-card-logo"
                        onError={(e) => { e.target.onerror = null; e.target.src = '/apple-touch-icon.png'; }}
                      />
                    </div>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#ffffff', marginBottom: '4px' }}>30 Hours</div>
                    <div style={{ fontSize: '0.85rem', color: '#C3DFC8' }}>1 Month Timeline</div>
                    <div style={{ fontSize: '0.85rem', color: '#C3DFC8' }}>85+ Exercises &amp; Movements</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 15. COURSE NOTICE */}
        <div className="fy-container">
          <div className="fy-page-disclaimer">
            <strong>Important Course Notice:</strong> This course is designed for educational, wellness, and teacher-training purposes. Face Yoga practices are natural wellness exercises and do not diagnose, treat, or cure medical skin or facial conditions or replace medical care by a dermatologist or healthcare professional. Participants should practice within comfortable physical limits and seek appropriate professional guidance when needed. Individual results vary.
          </div>
        </div>

      </main>

      <CourseEnrollModal
        isOpen={isEnrollModalOpen}
        onClose={() => setIsEnrollModalOpen(false)}
        courseTitle={courseTitle}
        formData={formData}
        setFormData={setFormData}
        upcomingBatches={upcomingBatches}
        isSubmitting={isSubmitting}
        isSuccess={isSuccess}
        handleEnrollSubmit={handleEnrollSubmit}
      />

      <PublicFooter />
    </div>
  );
};

export default FaceYoga;
